import { useState } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoginModal from './LoginModal';

interface HeroSectionProps {
    onOpenDemo: () => void;
    loginOpen?: boolean;
    onOpenLogin?: () => void;
    onCloseLogin?: () => void;
}

const HeroSection = ({ onOpenDemo, loginOpen, onOpenLogin, onCloseLogin }: HeroSectionProps) => {
    const [internalLoginOpen, setInternalLoginOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Support both lifted state (from MarketingPage) and internal state
    const isLoginOpen = loginOpen ?? internalLoginOpen;
    const openLogin  = onOpenLogin  ?? (() => setInternalLoginOpen(true));
    const closeLogin = onCloseLogin ?? (() => setInternalLoginOpen(false));
    const { user } = useAuthContext();
    const navigate = useNavigate();

    return (
        <>
            {/* ── NAVBAR ── */}
            <nav className="gradient-bg text-white shadow-lg fixed top-0 w-full z-50">
                <div className="container mx-auto px-4 py-3 flex justify-between items-center">

                    {/* Logo */}
                    <a href="#hero" className="flex items-center space-x-2">
                        <img src="/logo_lockup_on_dark.svg" alt="StockEasy" className="h-10" />
                    </a>

                    {/* Desktop links */}
                    <div className="hidden md:flex items-center space-x-7 text-sm font-medium">
                        <a href="#features"     className="hover:text-blue-200 transition">Features</a>
                        <a href="#how-it-works" className="hover:text-blue-200 transition">How It Works</a>
                        <a href="#testimonials" className="hover:text-blue-200 transition">Testimonials</a>
                        <a href="#pricing"      className="hover:text-blue-200 transition">Pricing</a>
                        <a href="#faq"          className="hover:text-blue-200 transition">FAQ</a>
                        <a href="#contact"      className="hover:text-blue-200 transition">Contact</a>
                    </div>

                    {/* Desktop CTAs */}
                    <div className="hidden md:flex items-center space-x-3">
                        <button
                            onClick={onOpenDemo}
                            className="cta-amber text-white px-5 py-2 rounded-lg font-semibold text-sm shadow hover:shadow-lg transition"
                        >
                            <i className="fas fa-calendar-check mr-1"></i> Book a Demo
                        </button>
                        {user ? (
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="bg-white text-blue-600 px-4 py-2 rounded-lg font-medium text-sm hover:bg-blue-50 transition"
                            >
                                Dashboard
                            </button>
                        ) : (
                            <button
                                onClick={() => openLogin()}
                                className="bg-white text-blue-600 px-4 py-2 rounded-lg font-medium text-sm hover:bg-blue-50 transition"
                            >
                                Login
                            </button>
                        )}
                    </div>

                    {/* Hamburger */}
                    <button
                        onClick={() => setMobileMenuOpen(v => !v)}
                        className="md:hidden text-2xl focus:outline-none"
                    >
                        <i className="fas fa-bars"></i>
                    </button>
                </div>

                {/* Mobile menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden bg-blue-900 px-4 py-3 space-y-2 text-sm font-medium">
                        <a href="#features"     className="block py-1 hover:text-blue-200 transition" onClick={() => setMobileMenuOpen(false)}>Features</a>
                        <a href="#how-it-works" className="block py-1 hover:text-blue-200 transition" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
                        <a href="#testimonials" className="block py-1 hover:text-blue-200 transition" onClick={() => setMobileMenuOpen(false)}>Testimonials</a>
                        <a href="#pricing"      className="block py-1 hover:text-blue-200 transition" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
                        <a href="#faq"          className="block py-1 hover:text-blue-200 transition" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
                        <a href="#contact"      className="block py-1 hover:text-blue-200 transition" onClick={() => setMobileMenuOpen(false)}>Contact</a>
                        <button
                            onClick={() => { setMobileMenuOpen(false); onOpenDemo(); }}
                            className="w-full mt-2 cta-amber text-white px-4 py-2 rounded-lg font-semibold"
                        >
                            <i className="fas fa-calendar-check mr-1"></i> Book a Demo
                        </button>
                        {user ? (
                            <button
                                onClick={() => { setMobileMenuOpen(false); navigate('/dashboard'); }}
                                className="w-full bg-white text-blue-600 px-4 py-2 rounded-lg font-medium"
                            >
                                Dashboard
                            </button>
                        ) : (
                            <button
                                onClick={() => { setMobileMenuOpen(false); openLogin(); }}
                                className="w-full bg-white text-blue-600 px-4 py-2 rounded-lg font-medium"
                            >
                                Login
                            </button>
                        )}
                    </div>
                )}
            </nav>

            {/* ── HERO ── */}
            <section id="hero" className="hero-bg text-white min-h-screen flex items-center pt-16">
                <div className="container mx-auto px-4 py-24">
                    <div className="max-w-4xl mx-auto text-center">

                        {/* Trust badge */}
                        <div className="inline-flex items-center bg-white/10 border border-white/20 rounded-full px-5 py-2 mb-8 text-sm font-medium backdrop-blur-sm">
                            <i className="fas fa-shield-alt text-amber-400 mr-2"></i>
                            Trusted by car parts businesses across the UK
                        </div>

                        {/* Headline */}
                        <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
                            Stop Losing Money on<br />
                            <span className="text-amber-400">Wrong Deliveries.</span>
                        </h1>

                        {/* Sub-headline */}
                        <p className="text-xl md:text-2xl text-blue-100 mb-4 max-w-3xl mx-auto leading-relaxed">
                            StockEasy's AI scanner verifies every car part against your delivery notes —{' '}
                            <strong className="text-white">with or without a barcode</strong> — and generates audit-ready reports in seconds.
                        </p>

                        {/* Secondary proof line */}
                        <p className="text-blue-300 text-sm mb-10">
                            <i className="fas fa-check-circle text-green-400 mr-1"></i> Mobile app included &nbsp;|&nbsp;
                            <i className="fas fa-check-circle text-green-400 mr-1"></i> 15-minute onboarding
                        </p>

                        {/* CTA buttons */}
                        <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
                            <button
                                onClick={onOpenDemo}
                                className="cta-amber text-white px-10 py-4 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transition transform hover:-translate-y-1"
                            >
                                <i className="fas fa-calendar-check mr-2"></i> Book a Free Demo
                            </button>
                            <a
                                href="#how-it-works"
                                className="bg-white/15 border border-white/30 text-white px-10 py-4 rounded-xl font-semibold text-lg hover:bg-white/25 transition backdrop-blur-sm"
                            >
                                <i className="fas fa-play-circle mr-2"></i> See How It Works
                            </a>
                        </div>

                        {/* App store availability */}
                        <div className="flex justify-center gap-4 flex-wrap">
                            <div className="flex items-center bg-black/30 border border-white/20 rounded-xl px-5 py-3 space-x-3 opacity-80">
                                <i className="fab fa-apple text-2xl"></i>
                                <div className="text-left leading-tight">
                                    <div className="text-xs text-blue-200">Available on the</div>
                                    <div className="text-sm font-semibold">App Store</div>
                                </div>
                            </div>
                            <div className="flex items-center bg-black/30 border border-white/20 rounded-xl px-5 py-3 space-x-3 opacity-80">
                                <i className="fab fa-google-play text-2xl"></i>
                                <div className="text-left leading-tight">
                                    <div className="text-xs text-blue-200">Available on</div>
                                    <div className="text-sm font-semibold">Google Play</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {isLoginOpen && <LoginModal onClose={closeLogin} />}
        </>
    );
};

export default HeroSection;
